import { useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useBindGeoSheetByStructureMutation,
  useGetGeoAxisGridsQuery,
  useGetGeoIsoNodesQuery,
  useGetGeoSheetAxesQuery,
  useGetGeoSheetCoordsQuery,
  useGetGeoSheetStructureQuery,
} from '@shared/api';
import {
  fmtCoord,
  fmtNumber,
  isoNodeFor,
  type GeoAxisGrid,
  type GeoIsoNode,
  type GeoStructureBindBody,
  type GeoStructureView,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  FieldGrid,
  FieldLabel,
  Hint,
  Notice,
  NumInput,
  Row,
  Stack,
  useAction,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

/** Метров натуры на пункт листа PDF в масштабе 1:1. */
const PT_M = 0.0254 / 72;
/** Масштабы опалубочных чертежей — для ручного выбора. */
const PLAN_SCALES = [5, 10, 15, 20, 25, 40, 50, 75, 100, 150, 200];

const Box = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h4`
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Legend = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

type PositionMode = 'cross' | 'catalog' | 'iso' | 'point';
type RotationMode = 'grid' | 'azimuth' | 'north';

/** «Рсм12 — X 18904,000, Y 14997,000, EL 101,350 (ISO-0001)». */
function isoNodeText(n: GeoIsoNode): string {
  const what = n.labels.length ? n.labels.join(', ') : 'узел';
  const el = n.h != null ? `, EL ${fmtCoord(n.h, 3)}` : '';
  return `${what} — X ${fmtCoord(n.x)}, Y ${fmtCoord(n.y)}${el} (${n.designation || n.file_name})`;
}

/** «1-А» → «1», «А». */
function splitCross(name: string): [string, string] | null {
  const i = name.indexOf('-');
  return i > 0 ? [name.slice(0, i), name.slice(i + 1)] : null;
}

function axesOf(grid: GeoAxisGrid | undefined): { digits: string[]; letters: string[] } {
  const digits = new Set<string>();
  const letters = new Set<string>();
  for (const p of grid?.points ?? []) {
    const parts = splitCross(p.name);
    if (!parts) continue;
    digits.add(parts[0]);
    letters.add(parts[1]);
  }
  const byNum = (a: string, b: string) => a.localeCompare(b, 'ru', { numeric: true });
  return { digits: [...digits].sort(byNum), letters: [...letters].sort(byNum) };
}

/** «точки съёмки: …» → «Точки съёмки: …». */
function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function centerText(s: GeoStructureView): string {
  if (s.center_by === 'axes') return 'пересечение осей конструкции на листе';
  if (s.center_by === 'circle') return 'центр окружности';
  return 'центр габарита (осей на плане не нашлось)';
}

/**
 * Лист одной конструкции (шаг 2): опалубочный чертёж ростверка, фундамента,
 * колонны — координат площадки на нём нет. Программа находит план конструкции
 * (под заголовком вида с её маркой), масштаб плана по размерам и точку привязки
 * (оси конструкции); геодезист говорит, где эта точка на площадке (пересечение
 * осей объекта, точка каталога или координаты) и как повёрнут план. Четверть
 * оборота не задана — программа подбирает её по съёмке.
 */
export function StructureBindPanel({
  draftPoints,
  dirty,
  onDone,
}: {
  /** Сколько точек привязки сейчас в черновике шага — их заменит привязка. */
  draftPoints: number;
  /** Есть несохранённые правки привязки. */
  dirty: boolean;
  /** Сбросить черновик шага — он подхватит точки с сервера. */
  onDone: () => void;
}) {
  const { scheme, readOnly } = useWizard();
  const sheet = scheme.sheet;
  const binding = scheme.settings.binding;
  const bound = Boolean(binding.result);
  const byStructure = binding.structure ?? null;
  const skip = !sheet || (bound && !byStructure);
  const { data, isFetching } = useGetGeoSheetStructureQuery(
    { id: scheme.id, fileId: sheet?.file_id ?? '', page: sheet?.page ?? 0 },
    { skip },
  );
  const { data: gridsData } = useGetGeoAxisGridsQuery(scheme.id, { skip: skip || readOnly });
  // Выноски координат и оси здания на листе — это схема расположения, а не
  // лист одной конструкции: панель свёрнута (кеш общий с их панелями).
  const sheetArgs = { id: scheme.id, fileId: sheet?.file_id ?? '', page: sheet?.page ?? 0 };
  const refsSkip = skip || readOnly || sheet?.kind !== 'pdf';
  const { data: coords } = useGetGeoSheetCoordsQuery(sheetArgs, { skip: refsSkip });
  const { data: sheetAxes } = useGetGeoSheetAxesQuery(sheetArgs, { skip: refsSkip });
  const grids = gridsData?.grids ?? [];
  // Изометрия в комплекте — её узлы с координатами годятся в центр конструкции.
  const hasIso = (scheme.sheets ?? []).some((sh) => sh.role === 'isometric');
  const { data: isoData } = useGetGeoIsoNodesQuery(scheme.id, {
    skip: skip || readOnly || !hasIso,
  });
  const isoNodes = isoData?.nodes ?? [];
  const catalog = useMemo(
    () =>
      scheme.design.elements.filter(
        (e) => !e.contour && (e.source === 'catalog' || e.source === 'manual') && e.label.trim(),
      ),
    [scheme.design.elements],
  );
  const [bind] = useBindGeoSheetByStructureMutation();
  const action = useAction();
  const [open, setOpen] = useState<boolean | null>(null);
  const [pick, setPick] = useState(0);
  const [scale, setScale] = useState<number | null>(null);
  const [posMode, setPosMode] = useState<PositionMode | null>(null);
  const [gridId, setGridId] = useState('');
  const [axisA, setAxisA] = useState('');
  const [axisB, setAxisB] = useState('');
  const [catalogId, setCatalogId] = useState('');
  const [isoPick, setIsoPick] = useState<number | null>(null);
  const [x, setX] = useState<number | null>(null);
  const [y, setY] = useState<number | null>(null);
  const [rotMode, setRotMode] = useState<RotationMode | null>(null);
  const [azimuth, setAzimuth] = useState<number | null>(null);
  const [quarter, setQuarter] = useState<'auto' | '0' | '1' | '2' | '3'>('auto');
  /** Ответ сервера, который ещё не дошёл до карточки в кеше. */
  const [pending, setPending] = useState<string | null>(null);
  const bindingKey = JSON.stringify(binding);

  useLayoutEffect(() => {
    if (!pending || pending !== bindingKey) return;
    onDone();
    setPending(null);
  }, [pending, bindingKey, onDone]);

  const structures = data?.structures ?? [];
  const s = structures[Math.min(pick, structures.length - 1)];
  const grid = grids.find((g) => g.id === gridId) ?? grids[0];
  const axes = axesOf(grid);
  // Узел изометрии с маркой конструкции — сразу центром; узел без марки — только выбором.
  const isoMatch = s ? isoNodeFor(isoNodes, s.label) : null;
  const isoNode = isoPick != null ? isoNodes[isoPick] : isoMatch;
  const mode: PositionMode =
    posMode ?? (isoMatch ? 'iso' : grids.length ? 'cross' : catalog.length ? 'catalog' : 'point');
  const rotation: RotationMode = rotMode ?? (grids.length ? 'grid' : 'azimuth');

  const fact = byStructure ? (
    <Notice $tone="success" role="status">
      Лист привязан по конструкции{byStructure.label ? ` ${byStructure.label}` : ''}:{' '}
      {byStructure.how}.{byStructure.check ? ` ${sentence(byStructure.check)}.` : ''}
    </Notice>
  ) : null;
  // Лист уже привязан иначе, план конструкции не найден или правка закрыта —
  // остаётся только сводка привязки по конструкции, если она есть.
  if (skip || !s || readOnly) {
    if (!fact || (!s && isFetching)) return null;
    return (
      <Box aria-labelledby="geo-structbind-title">
        <Title id="geo-structbind-title">Лист одной конструкции</Title>
        {fact}
      </Box>
    );
  }

  const siteRefs = (coords?.callouts.length ?? 0) >= 2 || (sheetAxes?.crosses.length ?? 0) >= 2;
  const expanded = open ?? (!byStructure && !siteRefs);
  const isPdf = sheet?.kind === 'pdf';
  const chosenScale = scale ?? s.scale;
  const scales =
    s.scale && !PLAN_SCALES.includes(s.scale)
      ? [...PLAN_SCALES, s.scale].sort((a, b) => a - b)
      : PLAN_SCALES;
  const unitM = isPdf ? (chosenScale ? chosenScale * PT_M : null) : s.unit_m;
  const sizeText = s.size_m ? `${fmtCoord(s.size_m[0], 3)} × ${fmtCoord(s.size_m[1], 3)} м` : null;

  const position = (): GeoStructureBindBody['position'] | string => {
    if (mode === 'cross') {
      if (!grid || !axisA || !axisB)
        return 'Выберите оси объекта и пересечение, где стоит конструкция.';
      return { kind: 'cross', grid_id: grid.id, a: axisA, b: axisB };
    }
    if (mode === 'catalog') {
      const el = catalog.find((e) => e.id === catalogId);
      if (!el) return 'Выберите точку каталога — центр конструкции.';
      return { kind: 'point', x: el.x, y: el.y, name: el.label };
    }
    if (mode === 'iso') {
      if (!isoNode) return 'Выберите узел изометрии — центр конструкции.';
      const name = isoNode.labels[0] ?? isoNode.designation ?? null;
      return { kind: 'point', x: isoNode.x, y: isoNode.y, name, source: 'iso' };
    }
    if (x == null || y == null) return 'Впишите X и Y центра конструкции.';
    return { kind: 'point', x, y };
  };

  const run = async () => {
    const pos = position();
    if (typeof pos === 'string') {
      action.setError(pos);
      return;
    }
    if (!unitM) {
      action.setError('Выберите масштаб плана — по размерам он не определился.');
      return;
    }
    if (rotation === 'azimuth' && azimuth == null) {
      action.setError('Впишите дирекционный угол оси здания (или выберите «север — вверх листа»).');
      return;
    }
    if (
      draftPoints > 0 &&
      !window.confirm(
        dirty
          ? 'Несохранённые точки привязки заменятся привязкой по конструкции. Продолжить?'
          : 'Текущие точки привязки заменятся привязкой по конструкции. Продолжить?',
      )
    )
      return;
    const body: GeoStructureBindBody = {
      u: s.center[0],
      v: s.center[1],
      unit_m: unitM,
      position: pos,
      rotation: {
        grid_id: rotation === 'grid' ? (grid?.id ?? null) : null,
        azimuth_deg: rotation === 'azimuth' ? azimuth : null,
        quarter: quarter === 'auto' ? null : Number(quarter),
      },
      outline: s.outline,
      label: s.label,
      scale: isPdf ? chosenScale : null,
    };
    const res = await action.runValue(
      () => bind({ id: scheme.id, ...body }).unwrap(),
      'Лист по конструкции не привязан.',
    );
    if (res) setPending(JSON.stringify(res.settings.binding));
  };

  if (!expanded) {
    return (
      <Box aria-labelledby="geo-structbind-title">
        <Head>
          <Title id="geo-structbind-title">Лист одной конструкции</Title>
          <Button type="button" $variant="secondary" $size="sm" onClick={() => setOpen(true)}>
            {byStructure ? 'Привязать заново…' : 'Привязать по конструкции…'}
          </Button>
        </Head>
        {fact ?? (
          <Hint>
            На листе есть план {s.label ?? 'конструкции'}
            {sizeText ? ` (${sizeText})` : ''}. Если это опалубочный чертёж одной конструкции без
            координат площадки — привяжите лист по её положению на площадке.
          </Hint>
        )}
      </Box>
    );
  }

  return (
    <Box aria-labelledby="geo-structbind-title">
      <Head>
        <Title id="geo-structbind-title">Лист одной конструкции</Title>
        <Row $gap={2}>
          {(byStructure || siteRefs) && (
            <Button type="button" $variant="ghost" $size="sm" onClick={() => setOpen(false)}>
              Свернуть
            </Button>
          )}
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => void run()}
            disabled={action.busy}
          >
            {action.busy ? 'Привязываем…' : 'Привязать по конструкции'}
          </Button>
        </Row>
      </Head>
      <Hint>
        {s.label ? `Конструкция ${s.label}` : 'Конструкция'}
        {s.title && s.title !== s.label ? ` «${s.title}»` : ''}
        {sizeText ? ` — ${sizeText}` : ''}
        {s.scale ? `, план 1:${s.scale} (по размерам: совпало ${s.dims})` : ''}. Точка привязки —{' '}
        {centerText(s)}. Координат площадки на листе нет: укажите, где эта точка на площадке и как
        повёрнут план — дальше всё как обычно.
      </Hint>
      {fact}
      <Stack $gap={3}>
        <FieldGrid $min={200}>
          {structures.length > 1 && (
            <FieldLabel>
              Конструкция на листе
              <Select
                value={pick}
                onChange={(e) => {
                  setPick(Number(e.target.value));
                  setScale(null);
                }}
              >
                {structures.map((st, i) => (
                  <option key={`${st.label ?? 'к'}-${i}`} value={i}>
                    {st.label ?? `контур ${i + 1}`}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          )}
          {isPdf && (
            <FieldLabel>
              Масштаб плана
              <Select
                value={chosenScale ?? ''}
                onChange={(e) => setScale(e.target.value ? Number(e.target.value) : null)}
              >
                {!chosenScale && <option value="">— выберите —</option>}
                {scales.map((n) => (
                  <option key={n} value={n}>
                    1:{n}
                    {n === s.scale ? ' (по размерам)' : ''}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          )}
        </FieldGrid>

        <Legend>Где центр конструкции на площадке</Legend>
        <FieldGrid $min={180}>
          <FieldLabel>
            Откуда
            <Select value={mode} onChange={(e) => setPosMode(e.target.value as PositionMode)}>
              {grids.length > 0 && <option value="cross">пересечение осей объекта</option>}
              {catalog.length > 0 && <option value="catalog">точка каталога</option>}
              {isoNodes.length > 0 && <option value="iso">узел изометрии</option>}
              <option value="point">координаты X, Y</option>
            </Select>
          </FieldLabel>
          {mode === 'iso' && (
            <FieldLabel>
              Узел изометрии
              <Select
                value={isoNode ? isoNodes.indexOf(isoNode) : ''}
                onChange={(e) => setIsoPick(e.target.value === '' ? null : Number(e.target.value))}
              >
                {!isoNode && <option value="">— выберите —</option>}
                {isoNodes.map((n, i) => (
                  <option key={`${n.file_id}-${i}`} value={i}>
                    {isoNodeText(n)}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          )}
          {mode === 'cross' && grid && (
            <>
              {grids.length > 1 && (
                <FieldLabel>
                  Оси объекта
                  <Select value={grid.id} onChange={(e) => setGridId(e.target.value)}>
                    {grids.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Select>
                </FieldLabel>
              )}
              <FieldLabel>
                Ось (цифра)
                <Select value={axisA} onChange={(e) => setAxisA(e.target.value)}>
                  <option value="">—</option>
                  {axes.digits.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </Select>
              </FieldLabel>
              <FieldLabel>
                Ось (буква)
                <Select value={axisB} onChange={(e) => setAxisB(e.target.value)}>
                  <option value="">—</option>
                  {axes.letters.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              </FieldLabel>
            </>
          )}
          {mode === 'catalog' && (
            <FieldLabel>
              Точка каталога
              <Select value={catalogId} onChange={(e) => setCatalogId(e.target.value)}>
                <option value="">— выберите —</option>
                {catalog.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.label} — X {fmtCoord(e.x)}, Y {fmtCoord(e.y)}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          )}
          {mode === 'point' && (
            <>
              <FieldLabel>
                X (север), м
                <NumInput value={x} onValue={setX} aria-label="X центра конструкции, м" />
              </FieldLabel>
              <FieldLabel>
                Y (восток), м
                <NumInput value={y} onValue={setY} aria-label="Y центра конструкции, м" />
              </FieldLabel>
            </>
          )}
        </FieldGrid>
        {mode === 'iso' && isoNode && (
          <Hint>
            Центр — узел {isoNode.labels[0] ? `«${isoNode.labels[0]}» ` : ''}изометрии{' '}
            {isoNode.designation || isoNode.file_name}: X {fmtCoord(isoNode.x)}, Y{' '}
            {fmtCoord(isoNode.y)}
            {isoNode.h != null
              ? `; EL ${fmtCoord(isoNode.h, 3)} — отметка узла трубопровода, к ярусам конструкции не применяется`
              : ''}
            . Узел — ось трубопровода: центром он годится, если конструкция стоит под ним по центру;
            иначе задайте центр точкой каталога или X, Y.
          </Hint>
        )}

        <Legend>Как повёрнут план</Legend>
        <FieldGrid $min={180}>
          <FieldLabel>
            Направление
            <Select value={rotation} onChange={(e) => setRotMode(e.target.value as RotationMode)}>
              {grids.length > 0 && <option value="grid">как оси объекта</option>}
              <option value="azimuth">по дирекционному углу оси здания</option>
              <option value="north">север — вверх листа</option>
            </Select>
          </FieldLabel>
          {rotation === 'azimuth' && (
            <FieldLabel>
              Дирекционный угол оси, °
              <NumInput
                value={azimuth}
                onValue={setAzimuth}
                aria-label="Дирекционный угол оси, градусы"
              />
            </FieldLabel>
          )}
          <FieldLabel>
            Поворот плана к оси (против часовой)
            <Select value={quarter} onChange={(e) => setQuarter(e.target.value as typeof quarter)}>
              <option value="auto">подобрать по съёмке</option>
              <option value="0">0°</option>
              <option value="1">90°</option>
              <option value="2">180°</option>
              <option value="3">270°</option>
            </Select>
          </FieldLabel>
        </FieldGrid>
        {quarter === 'auto' && scheme.survey.points.length === 0 && (
          <Hint>
            Съёмки ещё нет — поворот будет 0°: проверьте его по плану расположения или загрузите
            съёмку и привяжите заново.
          </Hint>
        )}
        {unitM && !isPdf && (
          <Hint>Единицы листа — {fmtNumber(unitM * 1000, 3)} мм (по размерам чертежа).</Hint>
        )}
      </Stack>
      {action.error && (
        <Notice $tone="danger" role="alert">
          {action.error}
        </Notice>
      )}
    </Box>
  );
}
